import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../atoms/dialog';
import { Button } from '../atoms/button';
import { Play, X } from 'lucide-react';
import { EventType, addNewEvent } from '@ideoz/core';

interface WhatIsProjectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  isLoggedIn: boolean;
  onCreateProject: () => void;
  onCreateAccount: () => void;
  isWeb?: boolean;
}

export const WhatIsProjectDialog: React.FC<WhatIsProjectDialogProps> = ({
  isOpen,
  onClose,
  isLoggedIn,
  onCreateProject,
  onCreateAccount,
  isWeb = true,
}) => {
  const [videoError, setVideoError] = useState(false);

  const handleVideoError = () => {
    setVideoError(true);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={`flex flex-col ${isWeb ? 'max-w-[600px] min-w-[600px]' : 'max-w-[480px] min-w-[480px]'}`}
      >
        <DialogHeader className="flex items-center justify-between">
          <DialogTitle>
            <span>What is Ideoz Project?</span>
          </DialogTitle>
          <button onClick={onClose} className="dialog-close-button">
            <X color="#667085" strokeWidth={2.5} />
          </button>
        </DialogHeader>

        <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
  {isWeb ? (
    /* WEB:  */
    <iframe
      src="https://www.youtube.com/embed/D6otuhiAjzQ?autoplay=1&mute=1&playsinline=1"
      title="Ideoz Project Video"
      frameBorder="0"
      allow="autoplay; encrypted-media; picture-in-picture"
      allowFullScreen
      className="w-full h-full"
    />
  ) : (
    /* PLUGIN*/
    <button
      onClick={() =>
        window.open(
          'https://www.youtube.com/watch?v=D6otuhiAjzQ',
          '_blank',
          'noopener,noreferrer'
        )
      }
      className="w-full h-full relative group"
    >
      <img
        src="https://img.youtube.com/vi/D6otuhiAjzQ/maxresdefault.jpg"
        alt="Ideoz video thumbnail"
        className="w-full h-full object-cover"
      />

      <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition">
        <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center">
          <Play size={28} />
        </div>
      </div>
    </button>
  )}
</div>


        <p className={`dialog-description`}>
          In Ideoz, a project is your space to scope, explore, and frame the right problem before you design. To
          create a project, you need to create a free account.
        </p>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3">
          <Button
            variant="secondary-gray"
            size={isWeb ? 'sm' : 'xs'}
            onClick={onClose}
            data-testid="what-is-project-cancel-button"
          >
            Cancel
          </Button>
          {isLoggedIn ? (
            <Button
              variant="primary"
              size={isWeb ? 'sm' : 'xs'}
              type="submit"
              onClick={() => {
                onCreateProject();
                addNewEvent(EventType.ClickOnCreateProjectAfterVideo, { location: 'after`video dialog' });
              }}
              data-testid="what-is-project-create-project-button"
            >
              Create New Project
            </Button>
          ) : (
            <Button
              variant="primary"
              size={isWeb ? 'sm' : 'xs'}
              type="submit"
              onClick={() => {
                onCreateAccount();
                addNewEvent(EventType.ClickOnCreateAccountAfterVideo, { location: 'after`video dialog' });
              }}
              data-testid="what-is-project-create-account-button"
            >
              Create Free Account
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
